import { settings } from "../Settings";
import { ringCentralCallStatus } from "../api/apiManager";

const normalizeDialString = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/[^\d*#+]/g, "")
    .replace(/^\+/, "");
};

export const CALL_STATUS = {
  IDLE: "Idle",
  CALLING: "Calling...",
  TRYING: "Trying...",
  RINGING: "Ringing...",
  CONNECTED: "Connected",
  IVR: "IVR Menu",
  HOLD: "On Hold",
  DISCONNECTED: "Call Ended",
  FAILED: "Failed",
  BUSY: "Busy",
  NO_ANSWER: "No Answer",
};

class RingCentralWebPhoneService {
  constructor() {
    this.webPhone = null;
    this.currentSession = null;
    this.isReady = false;
    this.isConnecting = false;
    this.lastStatus = CALL_STATUS.IDLE;
    this.lastError = "";
    this.listeners = new Set();
    this._silenceInterval = null;
    this._stateInterval = null;
    this._disposeInterval = null;
    this._connectedTimer = null;
    this._dialing = false;
    this._outboundHandler = null;
    // Per-call sipClient "inboundMessage" listener; tracked so it can be removed
    // on the next call / reset instead of leaking across calls.
    this._sipInboundHandler = null;
    this._telephonyInterval = null;
    this._dialedNumber = null;
    // App user id of the current registration — used to tell the backend WHICH
    // pooled extension to query for call status (active-calls is extension-scoped).
    this._appUserId = null;
    // Inbound calls: the pending (not-yet-answered) incoming session and its
    // listener/cancel watcher.
    this.incomingSession = null;
    this._inboundHandler = null;
    this._incomingDisposeInterval = null;
  }

  // =====================
  // STATE
  // =====================
  getState() {
    return {
      isReady: this.isReady,
      isConnecting: this.isConnecting,
      lastError: this.lastError,
      currentSession: this.currentSession,
      status: this.lastStatus,
    };
  }

  getCurrentSession() {
    return this.currentSession;
  }

  // True while a call is live, ringing, or mid-dial. Used to avoid tearing down
  // / re-registering the softphone during an active call.
  isOnCall() {
    return !!(this.currentSession || this.incomingSession || this._dialing);
  }

  // Best-effort check of the underlying SIP WebSocket. Returns true (alive),
  // false (definitely closed/closing), or null (unknown SDK shape -> don't act).
  // Used by the keep-alive so it only re-registers when the link is really down.
  isConnectionAlive() {
    try {
      const ws =
        this.webPhone?.sipClient?.wsc ||
        this.webPhone?.sipClient?.ws ||
        this.webPhone?.sipClient?.webSocket ||
        this.webPhone?.client?.wsc;
      if (!ws || typeof ws.readyState !== "number") return null;
      return ws.readyState === 1; // WebSocket.OPEN
    } catch (e) {
      return null;
    }
  }

  _isDeadState(session) {
    const st = session?.state;
    return (
      !session ||
      st === "disposed" ||
      st === "failed" ||
      st === "terminated"
    );
  }

  // A previous call's session reference can linger if its dispose poll never
  // saw a terminal state. Such a stale reference would make the inbound guard
  // wrongly decline the NEXT incoming call (it "looks busy"). Clear references
  // that are actually dead so a genuinely-idle phone accepts new calls.
  _clearDeadSessions() {
    if (this.currentSession && this._isDeadState(this.currentSession)) {
      this._stopAllTimers();
      this.currentSession = null;
    }
    if (this.incomingSession && this._isDeadState(this.incomingSession)) {
      if (this._incomingDisposeInterval) {
        clearInterval(this._incomingDisposeInterval);
        this._incomingDisposeInterval = null;
      }
      this.incomingSession = null;
    }
  }

  // =====================
  // EVENTS
  // =====================
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(payload) {
    this.listeners.forEach((fn) => fn(payload));
  }

  setStatus(status) {
    if (this.lastStatus === status) return;
    this.lastStatus = status;
    this.emit({ type: "status", status });
  }

  // =====================
  // SDK
  // =====================
  getSdk() {
    const SDK = window?.WebPhone;
    if (!SDK) throw new Error("WebPhone SDK not found");
    return SDK;
  }

  // =====================
  // AUDIO
  // =====================
  _startSilenceInterval() {
    // Intentionally a no-op. This used to force-mute every <audio> element once
    // per second to suppress the SDK's ringback tone — but ringtones are already
    // disabled (audioHelper.enabled = false), so the loop served no purpose and
    // risked periodically muting the live call audio (causing voice to drop in
    // and out). Kept as a no-op so existing call sites don't break.
    this._stopSilenceInterval();
  }

  _stopSilenceInterval() {
    if (this._silenceInterval) {
      clearInterval(this._silenceInterval);
      this._silenceInterval = null;
    }
  }

  // =====================
  // INIT
  // =====================
  async init(data = {}) {
    // Idempotent: if a softphone is already registered, reuse it. Creating a
    // second instance opens another SIP registration, which makes RingCentral
    // fork the outbound call so the recipient receives multiple simultaneous legs.
    if (this.isReady && this.webPhone) {
      return true;
    }

    try {
      this.isConnecting = true;

      const SDK = this.getSdk();
      const sipInfo = data?.sipInfo;

      if (!sipInfo) throw new Error("SIP info missing");

      // Remember the last init payload so a recovery re-register can fall back
      // to it if a fresh sip-info fetch isn't available.
      this._lastInitData = data;
      this._appUserId = data?.appUserId || this._appUserId;

      this.webPhone = new SDK({
        sipInfo,
        // Stable per-user SIP instance id so a refresh re-registers the SAME device
        // instead of adding another registration (avoids duplicate call legs).
        instanceId: `maxchats-${data?.appUserId || "web"}`,
        clientId: data?.clientId,
        debug: false,
        audioHelper: {
          enabled: false,
          incoming: null,
          outgoing: null,
        },
        enableQos: false,
        enableMusicOnHold: false,
      });

      await this.webPhone.start();

      // Register the incoming-call listener now that the softphone is up, so
      // calls to the RingCentral number ring inside the app.
      this._attachInboundHandler();

      this.isReady = true;
      this.setStatus(CALL_STATUS.IDLE);

      return true;
    } catch (err) {
      this.lastError = err?.message;
      this.isReady = false;
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  // Tear down the current softphone so a fresh init() can cleanly re-register.
  // The stable instanceId means re-registering REPLACES this device's SIP
  // registration rather than adding a duplicate leg. Callers MUST guard on
  // isOnCall() — never reset during a live/ringing/dialing call.
  async reset() {
    try {
      if (this._inboundHandler && this.webPhone?.off) {
        this.webPhone.off("inboundCall", this._inboundHandler);
      }
      if (this._outboundHandler && this.webPhone?.off) {
        this.webPhone.off("outboundCall", this._outboundHandler);
      }
      if (this._sipInboundHandler && this.webPhone?.sipClient?.off) {
        this.webPhone.sipClient.off("inboundMessage", this._sipInboundHandler);
      }
      await this.webPhone?.dispose?.();
    } catch (e) {
      /* ignore — phone may already be gone */
    }
    this._stopAllTimers();
    this.webPhone = null;
    this.isReady = false;
    this._inboundHandler = null;
    this._outboundHandler = null;
    this._sipInboundHandler = null;
    // Intentionally leave currentSession/incomingSession alone; isOnCall()
    // guards prevent reset() from being called while one exists.
  }

  // =====================
  // CALL
  // =====================

  async call(to, callerId) {
    if (!this.isReady) throw new Error("WebPhone not ready");

    // Guard against overlapping dials (double-click, re-render, or multiple code
    // paths calling this). Without it the recipient gets several simultaneous legs.
    if (this.currentSession || this._dialing) {
      console.warn("[RC] Call already in progress; ignoring duplicate dial");
      return this.currentSession;
    }
    this._dialing = true;

    // Remember the dialed number so the telephony-status poller can match it
    // against RingCentral's active-call list.
    this._dialedNumber = to;

    const number = normalizeDialString(to);
    this.setStatus(CALL_STATUS.CALLING);
    this._startSilenceInterval();

    // Register the outbound handler once; remove any previous one first so handlers
    // don't accumulate across calls on the persistent WebPhone instance.
    if (this._outboundHandler) {
      this.webPhone.off?.("outboundCall", this._outboundHandler);
    }
    this._outboundHandler = (session) => {
      this.currentSession = session;
      window.__rcSession = session;
      this._pollSessionState(session);
    };
    this.webPhone.on("outboundCall", this._outboundHandler);

    // sipClient messages monitor karo - reject detect karne ke liye.
    // Parse the SIP status line so ANY final failure response (4xx/5xx/6xx)
    // surfaces. The old code only caught 487/486/603/480, so a 403 (caller ID
    // not allowed), 404 (bad number), 408 (timeout) or 488/5xx left the UI stuck
    // on "Calling..." forever.
    const sipClient = this.webPhone?.sipClient;
    if (sipClient) {
      const handleInbound = (message) => {
        const subject = message?.subject || "";

        // Status line looks like "SIP/2.0 403 Forbidden". Pull the response code.
        const match = subject.match(/SIP\/2\.0\s+(\d{3})/);
        const code = match ? parseInt(match[1], 10) : null;

        // 180 Ringing / 183 Session Progress -> remote end is alerting.
        if (code === 180 || code === 183) {
          this.setStatus(CALL_STATUS.TRYING);
          return;
        }

        // 401 Unauthorized / 407 Proxy Auth Required are the normal SIP digest
        // challenges — the SDK transparently re-sends the INVITE with credentials
        // and the call proceeds. They are NOT call failures, so ignore them (a
        // previous version treated any >=400 as failure, which tore down the
        // caller's screen on the auth challenge while the callee still rang).
        if (code === 401 || code === 407) {
          return;
        }

        // 481 (Call/Transaction Does Not Exist — "Dialog has gone") and 487
        // (Request Terminated) are normal END-OF-CALL artifacts, NOT dial
        // failures: they come back when a BYE/CANCEL races an already-gone
        // dialog — e.g. the answer-poller ends the call AND the user presses
        // End, or the callee hung up first. Treating them as FAILED logged a
        // scary "[RC SIP FAIL]" and could clobber the call's real end state, so
        // just ignore them (the telephony poller / dispose watcher handles the
        // real teardown).
        if (code === 481 || code === 487) {
          console.debug(`[RC SIP] benign teardown ${code}`, subject);
          return;
        }

        // 1xx provisional (100/180/183) and 2xx success are NOT failures.
        if (code !== null && code >= 400) {
          let status = CALL_STATUS.DISCONNECTED;
          if (code === 486 || code === 600 || code === 603)
            status = CALL_STATUS.BUSY;
          else if (code === 480 || code === 408)
            status = CALL_STATUS.NO_ANSWER;
          else status = CALL_STATUS.FAILED;

          console.warn(`[RC SIP FAIL] INVITE rejected with ${code}`, subject);
          this.lastError = `SIP ${code}: ${subject}`;
          this._stopAllTimers();
          this._stopSilenceInterval();
          this.currentSession = null;
          this.setStatus(status);
        }
      };
      // Remove any handler left over from a previous call on this persistent
      // sipClient so listeners don't accumulate across calls (they used to be
      // removed only when a >=400 arrived, leaking one per successful call).
      if (this._sipInboundHandler) {
        sipClient.off?.("inboundMessage", this._sipInboundHandler);
      }
      this._sipInboundHandler = handleInbound;
      sipClient.on("inboundMessage", handleInbound);
    }

    try {
      const session = await this.webPhone.call(number, callerId);
      // Ensure the session is tracked even if the outboundCall event didn't fire.
      if (session && !this.currentSession) {
        this.currentSession = session;
        window.__rcSession = session;
        this._pollSessionState(session);
      }
      console.log("[RC CALL RETURNED]", session);
      return session;
    } catch (err) {
      this._stopSilenceInterval();
      this.setStatus(CALL_STATUS.FAILED);
      throw err;
    } finally {
      this._dialing = false;
    }
  }

  // =====================
  // SESSION STATE
  // =====================

  _pollSessionState(session) {
    if (!session) return;
    this._stopAllTimers();
    this.setStatus(CALL_STATUS.CALLING);

    session.on("ringing", () => {
      this.setStatus(CALL_STATUS.TRYING);
    });

    // RingCentral answers the browser's WebRTC leg immediately to deliver the
    // ringback tone, so the SDK "answered" event fires while the PSTN party is
    // still ringing — it is NOT a real pickup. Treat it as "Ringing...", not
    // Connected. The true pickup is detected via RingCentral's telephony status
    // (see _startTelephonyStatusPolling), which flips the status to CONNECTED.
    session.on("answered", () => {
      if (this.lastStatus !== CALL_STATUS.CONNECTED) {
        this.setStatus(CALL_STATUS.TRYING);
      }
    });

    //  Dispose check
    const disposeCheck = setInterval(() => {
      if (session?.state === "disposed" || session?.state === "failed") {
        console.warn("[RC] call ended by dispose watcher; session.state=", session?.state);
        clearInterval(disposeCheck);
        this._disposeInterval = null;
        this._stopAllTimers();
        this.currentSession = null;
        this._stopSilenceInterval();
        this.setStatus(CALL_STATUS.DISCONNECTED);
        setTimeout(() => {
          this._pollCallLog();
        }, 3000);
      }
    }, 300);

    this._disposeInterval = disposeCheck;

    // Start polling RingCentral's real telephony status to detect the actual
    // PSTN pickup (Ringing -> CallConnected), since the SIP leg can't tell.
    this._startTelephonyStatusPolling();
  }

  // Poll the backend (which has the JWT access token) for the live telephony
  // status of this outbound call. RingCentral reports "Ringing" while the
  // callee's phone rings and "CallConnected" only when they truly pick up — so
  // this is what flips the UI from "Ringing..." to "Connected".
  _startTelephonyStatusPolling() {
    if (this._telephonyInterval) {
      clearTimeout(this._telephonyInterval);
      this._telephonyInterval = null;
    }

    // Track whether RingCentral has actually reported this call as live yet.
    let sawActiveCall = false;
    let noCallStreak = 0; // consecutive DEFINITIVE terminal ("NoCall") reads
    let notFoundStreak = 0; // consecutive ambiguous "NotFound" reads
    let stopped = false;

    // Base cadence. Was 1.5s, which (× concurrent pooled calls) hammered
    // RingCentral's active-calls endpoint past its low "Heavy"-group rate limit
    // and got a flood of 429s — and while rate-limited the "Ringing -> Connected"
    // flip couldn't fire. 3s keeps us under the limit; on a 429 we back off
    // further using the server's Retry-After. Thresholds are halved so end-of-
    // call detection stays about as fast as before (~6s live, ~12s backstop).
    const BASE_INTERVAL = 3000;

    const stop = () => {
      stopped = true;
      if (this._telephonyInterval) {
        clearTimeout(this._telephonyInterval);
        this._telephonyInterval = null;
      }
    };

    const poll = async () => {
      if (stopped) return;
      let nextDelay = BASE_INTERVAL;
      try {
        const res = await ringCentralCallStatus(this._dialedNumber, this._appUserId);
        const status = res?.data?.telephonyStatus;
        // Diagnostic: shows exactly what the poll sees each tick and the streaks
        // that drive end-of-call. Remove once the "rings then drops" is resolved.
        console.log(
          "[RC poll]",
          "status=", status,
          "partyStatus=", res?.data?.partyStatus,
          "noCallStreak=", noCallStreak,
          "notFoundStreak=", notFoundStreak,
          "uiStatus=", this.lastStatus,
        );

        if (status === "RateLimited") {
          // Backend was rate-limited by RingCentral. Back off for the advised
          // time (min the base cadence) and DON'T touch call state.
          const retryAfter = Number(res?.data?.retryAfter) || 5;
          nextDelay = Math.max(retryAfter * 1000, BASE_INTERVAL);
        } else if (status === "CallConnected") {
          // Real pickup. Flip to Connected (only from the ringing phase) and
          // stop polling — hold/end are handled elsewhere.
          sawActiveCall = true;
          noCallStreak = 0;
          if (
            this.lastStatus === CALL_STATUS.CALLING ||
            this.lastStatus === CALL_STATUS.TRYING
          ) {
            this.setStatus(CALL_STATUS.CONNECTED);
          }
          stop();
          return;
        } else if (status === "Ringing") {
          // Callee is being alerted but hasn't answered yet.
          sawActiveCall = true;
          noCallStreak = 0;
          notFoundStreak = 0;
          if (this.lastStatus === CALL_STATUS.CALLING) {
            this.setStatus(CALL_STATUS.TRYING);
          }
        } else if (status === "NoCall") {
          // Terminal party status from RC (Disconnected/Gone/VoiceMail/Rejected/
          // Busy/Parked). RC answers our browser leg for ringback so no SIP
          // 4xx/6xx arrives — this poll is the only pre-pickup "ended" signal.
          // Only a reliable "the CURRENT call ended" signal if we've actually
          // seen THIS call go live (Ringing/Connected) first. Before that, a
          // terminal read is almost always a STALE just-ended PREVIOUS call still
          // lingering in active-calls on this pooled extension (classic on
          // re-dial: "call again ends immediately") — killing the new call on it
          // is wrong. Seen-live -> real end after 2 reads; not-yet-live -> treat
          // as ambiguous like NotFound and wait for the new call to appear.
          if (sawActiveCall) {
            notFoundStreak = 0;
            noCallStreak += 1;
            if (noCallStreak >= 2) {
              this._endFromPoll(stop);
              return;
            }
          } else {
            noCallStreak = 0;
            notFoundStreak += 1;
            if (notFoundStreak >= 12) {
              this._endFromPoll(stop);
              return;
            }
          }
        } else if (status === "NotFound") {
          // AMBIGUOUS: the call isn't listed in active-calls. This happens for a
          // genuinely-ringing call that RC hasn't listed yet (eventual
          // consistency / rate-limiting) AND for a call that has ended. It is NOT
          // a reliable "ended" signal, so it must NOT kill a call that is still
          // ringing (this was the "rings a few seconds then 'Call could not be
          // connected'" bug). Real ends are caught by terminal "NoCall" above or
          // the SIP dispose watcher; only give up after a long fallback so an
          // orphaned session doesn't ring forever.
          noCallStreak = 0;
          notFoundStreak += 1;
          if (notFoundStreak >= 12) {
            this._endFromPoll(stop);
            return;
          }
        }
        // status falsy (no data) -> just keep polling at base cadence.
      } catch (e) {
        /* transient network error — keep polling */
      }

      if (!stopped) {
        this._telephonyInterval = setTimeout(poll, nextDelay);
      }
    };

    // Kick off immediately; subsequent polls self-schedule via setTimeout.
    this._telephonyInterval = setTimeout(poll, 0);
  }

  // End a call from the telephony poller (decline / no-answer / orphaned session).
  // Ended before pickup -> "Failed"; ended after pickup -> "Call Ended".
  _endFromPoll(stop) {
    console.warn("[RC] call ended by telephony poll (_endFromPoll)");
    this._stopSilenceInterval();
    try {
      this.currentSession?.hangup?.();
    } catch {}
    this.currentSession = null;
    this.setStatus(
      this.lastStatus === CALL_STATUS.CONNECTED
        ? CALL_STATUS.DISCONNECTED
        : CALL_STATUS.FAILED,
    );
    stop?.();
    this._stopAllTimers();
  }

  // ✅ Call log se status detect karo
  async _pollCallLog() {
    const token = localStorage.getItem("ringCentral_access_token");
    if (!token) return;

    let connectedDetected = false;
    let callStartTime = Date.now();

    // const logInterval = setInterval(async () => {
    try {
      const res = await fetch(
        "https://platform.ringcentral.com/restapi/v1.0/account/~/extension/~/call-log?perPage=1&view=Detailed",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      console.log("fetch status:", res);
      const data = await res.json();
      const record = data?.records?.[0];
      // Call connected
      if (record?.result === "Call connected" && !connectedDetected) {
        // Sirf recent call check karo (last 5 minutes)
        const recordTime = new Date(record?.startTime).getTime();
        const timeDiff = Date.now() - recordTime;
      }

      // ✅ Call end detect - disposed state
      // if (!this.currentSession) {
      //   clearInterval(logInterval);
      //   this._logInterval = null;
      // }
    } catch (e) {
      console.error("[RC CALL LOG] error:", e);
    }
    // }, 2000); // Har 2 second pe check

    // this._logInterval = logInterval;
  }

  // =====================
  // INBOUND CALLS
  // =====================

  // Wire the SDK's incoming-call event once. The WebPhone must be init()'d and
  // registered for this to fire, so init() is triggered on app load (not just
  // on first dial). Emits an "incoming" event the UI listens for.
  _attachInboundHandler() {
    if (!this.webPhone || this._inboundHandler) return;

    this._inboundHandler = (session) => {
      // Drop any leftover-but-dead session refs first, so a stale reference from
      // a previous call doesn't make us wrongly decline this new incoming call.
      this._clearDeadSessions();

      // Genuinely busy (live call or another incoming pending) -> politely decline.
      if (this.currentSession || this.incomingSession) {
        this._declineSession(session);
        return;
      }

      this.incomingSession = session;
      window.__rcIncoming = session;

      const callerId = this._extractCaller(session);

      // If the caller hangs up before we answer, clear the incoming UI.
      if (this._incomingDisposeInterval)
        clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = setInterval(() => {
        const st = session?.state;
        if (st === "disposed" || st === "failed" || st === "terminated") {
          clearInterval(this._incomingDisposeInterval);
          this._incomingDisposeInterval = null;
          if (this.incomingSession === session) {
            this.incomingSession = null;
            this.emit({ type: "incoming-cancelled" });
          }
        }
      }, 300);

      this.emit({ type: "incoming", session, callerId });
    };

    this.webPhone.on("inboundCall", this._inboundHandler);
  }

  // Best-effort caller-number extraction across SDK object shapes. The WebPhone
  // v2 session may expose the caller as `remoteNumber`, or only inside the SIP
  // INVITE's From header (e.g. '"Name" <sip:+12145551234@host>;tag=...'). We log
  // the session so unknown shapes can be inspected via the console.
  _extractCaller(session) {
    const sm = session?.sipMessage;
    try {
      // eslint-disable-next-line no-console
      console.log("[RC inbound] session:", session, "sipMessage.headers:", sm?.headers);
    } catch (e) {
      /* ignore */
    }

    // The caller is the SIP "From" header on the inbound INVITE. Try direct
    // getters first, then the From header (under a few possible shapes). Do NOT
    // scan the whole message — the Request-URI / To header carry OUR own number.
    const fromHeader =
      sm?.headers?.From ??
      sm?.headers?.from ??
      session?.inviteRequest?.headers?.From ??
      session?.request?.headers?.From;

    const candidates = [
      session?.remoteNumber,
      session?.fromNumber,
      session?.from,
      session?.callInfo?.from,
      session?.remoteIdentity?.uri?.user,
      fromHeader,
    ].filter(Boolean);

    for (const c of candidates) {
      const str = String(c);
      // From header looks like: '"Caller" <sip:+12145551234@host>;tag=...'.
      // Prefer the user part of the sip:/tel: URI; else fall back to the raw
      // value (a bare number), trimmed of any host after '@'.
      const uri = str.match(/(?:sip|tel):(\+?\d{6,})/i);
      const base = uri ? uri[1] : str.split("@")[0];
      const digits = base.replace(/[^\d]/g, "");
      if (digits.length >= 6) return `+${digits}`;
    }
    return "Unknown";
  }

  // Answer the pending incoming call.
  async acceptIncoming() {
    const session = this.incomingSession;
    if (!session) return null;
    this.incomingSession = null;
    if (this._incomingDisposeInterval) {
      clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = null;
    }

    this.currentSession = session;
    window.__rcSession = session;

    try {
      if (typeof session.answer === "function") await session.answer();
      else if (typeof session.accept === "function") await session.accept();
      else throw new Error("Incoming session has no answer()/accept()");
    } catch (err) {
      this.lastError = err?.message;
      this.currentSession = null;
      this.setStatus(CALL_STATUS.FAILED);
      throw err;
    }

    this.setStatus(CALL_STATUS.CONNECTED);
    this._watchSessionDispose(session);
    return session;
  }

  // Reject/decline the pending incoming call.
  async rejectIncoming() {
    const session = this.incomingSession;
    this.incomingSession = null;
    if (this._incomingDisposeInterval) {
      clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = null;
    }
    if (session) {
      await this._declineSession(session);
      // Let the UI log it as a missed/declined inbound call.
      this.emit({ type: "incoming-cancelled" });
    }
  }

  async _declineSession(session) {
    try {
      if (typeof session.decline === "function") await session.decline();
      else if (typeof session.reject === "function") await session.reject();
      else if (typeof session.toVoicemail === "function")
        await session.toVoicemail();
      else if (typeof session.hangup === "function") await session.hangup();
    } catch (e) {
      /* ignore — call may already be gone */
    }
  }

  // For an answered inbound call: end the UI when either side hangs up.
  _watchSessionDispose(session) {
    this._stopAllTimers();
    this._disposeInterval = setInterval(() => {
      const st = session?.state;
      if (st === "disposed" || st === "failed" || st === "terminated") {
        clearInterval(this._disposeInterval);
        this._disposeInterval = null;
        this._stopAllTimers();
        this.currentSession = null;
        this.setStatus(CALL_STATUS.DISCONNECTED);
      }
    }, 300);
  }

  // =====================
  // STOP ALL TIMERS
  // =====================
  _stopAllTimers() {
    if (this._stateInterval) {
      clearInterval(this._stateInterval);
      this._stateInterval = null;
    }
    if (this._disposeInterval) {
      clearInterval(this._disposeInterval);
      this._disposeInterval = null;
    }
    if (this._connectedTimer) {
      clearTimeout(this._connectedTimer);
      this._connectedTimer = null;
    }
    if (this._telephonyInterval) {
      // Now a self-scheduling setTimeout chain (see _startTelephonyStatusPolling).
      clearTimeout(this._telephonyInterval);
      this._telephonyInterval = null;
    }
  }

  // =====================
  // DTMF
  // =====================
  sendDTMF(digit) {
    try {
      if (!this.currentSession) {
        console.warn("[DTMF] No session!");
        return;
      }
      if (typeof this.currentSession.sendDtmf === "function") {
        this.currentSession.sendDtmf(digit);
        console.log("[DTMF] sent:", digit);
      } else {
        console.error("[DTMF] sendDtmf not found!");
      }
    } catch (e) {
      console.error("[DTMF] error:", e);
    }
  }

  async sendDTMFSequence(sequence, delay = 600) {
    if (!sequence) return;
    for (let i = 0; i < sequence.length; i++) {
      this.sendDTMF(sequence[i]);
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  // =====================
  // ACTIONS
  // =====================
  async hangup() {
    this._stopAllTimers();
    this._stopSilenceInterval();
    if (!this.currentSession) return;
    try {
      await this.currentSession.hangup?.();
    } catch {}
    this.currentSession = null;
    this.setStatus(CALL_STATUS.DISCONNECTED);
  }

  async mute(muted) {
    if (!this.currentSession) return;
    try {
      muted ? this.currentSession.mute?.() : this.currentSession.unmute?.();
    } catch {}
  }

  async hold(held) {
    if (!this.currentSession) return;
    try {
      if (held) {
        await this.currentSession.hold?.();
        this.setStatus(CALL_STATUS.HOLD);
      } else {
        await this.currentSession.unhold?.();
        this.setStatus(CALL_STATUS.CONNECTED);
      }
    } catch {}
  }
}

const ringCentralWebPhoneService = new RingCentralWebPhoneService();
window.__rcService = ringCentralWebPhoneService;
export default ringCentralWebPhoneService;
